import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Database, Mail, CheckCircle2, AlertCircle, XCircle, Sliders, Sparkles, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { connectors, defaultWeights, AlgorithmWeights } from '../lib/data';
function ConnectorStatus({
  status
}: {
  status: string;
}) {
  if (status === 'connected') {
    return <div className="flex items-center gap-1.5 text-sm text-sentiment-positive" data-id="element-1387">
        <CheckCircle2 className="h-3.5 w-3.5" data-id="element-1388" />
        Connesso
      </div>;
  }
  if (status === 'error') {
    return <div className="flex items-center gap-1.5 text-sm text-destructive" data-id="element-1389">
        <AlertCircle className="h-3.5 w-3.5" data-id="element-1390" />
        Errore
      </div>;
  }
  return <div className="flex items-center gap-1.5 text-sm text-muted-foreground" data-id="element-1391">
      <XCircle className="h-3.5 w-3.5" data-id="element-1392" />
      Non connesso
    </div>;
}
function ConnectorsTab() {
  return <div data-id="element-1393">
      <div className="mb-8" data-id="element-1394">
        <h2 className="text-lg font-semibold mb-1" data-id="element-1395">Integrazioni automatiche</h2>
        <p className="text-sm text-muted-foreground" data-id="element-1396">
          Configura i servizi per la raccolta automatica delle rassegne stampa.
        </p>
      </div>

      <div className="divide-y divide-border border-y border-border" data-id="element-1397">
        {connectors.map(connector => {
        const Icon = connector.id === 'email' ? Mail : Database;
        return <div key={connector.id} className="flex items-center gap-4 py-5 md:py-6" data-id="element-1398">
              <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center flex-shrink-0" data-id="element-1399">
                <Icon className="h-4 w-4 text-muted-foreground" data-id="element-1400" />
              </div>

              <div className="flex-1 min-w-0" data-id="element-1401">
                <div className="flex items-center gap-3 mb-1" data-id="element-1402">
                  <h3 className="font-medium text-base" data-id="element-1403">{connector.name}</h3>
                  <ConnectorStatus status={connector.status} data-id="element-1404" />
                </div>
                <p className="text-sm text-muted-foreground line-clamp-1" data-id="element-1405">
                  {connector.status === 'connected' && connector.lastSync ? <>
                      Ultimo sync{' '}
                      {new Date(connector.lastSync).toLocaleString('it-IT', {
                  day: '2-digit',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                      {' · '}
                      {connector.rassegneIngested} rassegne totali
                    </> : connector.description}
                </p>
              </div>

              <div className="flex-shrink-0" data-id="element-1406">
                {connector.status === 'disconnected' ? <Button size="sm" className="rounded-full" data-id="element-1407">
                    Connetti
                  </Button> : connector.status === 'error' ? <Button size="sm" className="rounded-full" data-id="element-1408">
                    Riprova
                  </Button> : <Button variant="outline" size="sm" className="rounded-full border-accent text-accent hover:bg-accent/10 hover:text-accent" data-id="element-1409">
                    Configura
                  </Button>}
              </div>
            </div>;
      })}
      </div>
    </div>;
}
function WeightRow({
  label,
  description,
  value,
  onChange,
  max = 3
}: {
  label: string;
  description?: string;
  value: number;
  onChange: (v: number) => void;
  max?: number;
}) {
  return <div className="grid grid-cols-1 md:grid-cols-[280px_1fr_50px] gap-2 md:gap-6 items-center py-4" data-id="element-1410">
      <div data-id="element-1411">
        <div className="text-sm font-medium" data-id="element-1412">{label}</div>
        {description && <div className="text-xs text-muted-foreground mt-0.5" data-id="element-1413">
            {description}
          </div>}
      </div>
      <Slider value={[value]} onValueChange={v => onChange(v[0])} max={max} step={0.1} data-id="element-1414" />
      <div className="text-sm font-medium text-right tabular-nums" style={{
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-1415">
        +{value.toFixed(1)}
      </div>
    </div>;
}
interface Profile {
  id: string;
  name: string;
  isActive: boolean;
  weights: AlgorithmWeights;
  createdAt: string;
}
const initialProfiles: Profile[] = [{
  id: '1',
  name: 'Profilo Standard',
  isActive: true,
  weights: defaultWeights,
  createdAt: '2025-01-10T10:00:00'
}, {
  id: '2',
  name: 'Focus Tier 1',
  isActive: false,
  weights: {
    ...defaultWeights,
    tier1: 3.0,
    tier2: 0.5,
    tier3: 0.2
  },
  createdAt: '2025-02-15T14:30:00'
}, {
  id: '3',
  name: 'Sentiment-driven',
  isActive: false,
  weights: {
    ...defaultWeights,
    sentimentPositive: 2.0,
    sentimentNegative: 2.0
  },
  createdAt: '2025-03-20T09:15:00'
}];
function getCustomizedCount(weights: AlgorithmWeights) {
  let count = 0;
  for (const key in weights) {
    if (weights[key as keyof AlgorithmWeights] !== defaultWeights[key as keyof AlgorithmWeights]) {
      count++;
    }
  }
  return count;
}
function ProfileEditor({
  profile,
  onSave,
  onCancel
}: {
  profile: Profile;
  onSave: (w: AlgorithmWeights) => void;
  onCancel: () => void;
}) {
  const [weights, setWeights] = useState<AlgorithmWeights>(profile.weights);
  const update = (key: keyof AlgorithmWeights, value: number) => {
    setWeights(w => ({
      ...w,
      [key]: value
    }));
  };
  const sections: Array<{
    title: string;
    description: string;
    items: Array<{
      key: keyof AlgorithmWeights;
      label: string;
      description?: string;
      max?: number;
    }>;
  }> = [{
    title: 'Tier testata',
    description: 'Peso assegnato in base alla rilevanza editoriale della testata.',
    items: [{
      key: 'tier1',
      label: 'Tier 1',
      description: 'Testate principali (Sole 24 Ore, Corriere…)',
      max: 3
    }, {
      key: 'tier2',
      label: 'Tier 2',
      description: 'Testate secondarie',
      max: 3
    }, {
      key: 'tier3',
      label: 'Tier 3',
      description: 'Testate minori',
      max: 3
    }, {
      key: 'tierSpecialized',
      label: 'Specializzata',
      description: 'Settoriali e di nicchia',
      max: 3
    }]
  }, {
    title: 'Posizionamento',
    description: "Bonus per elementi di visibilità nell'articolo.",
    items: [{
      key: 'positioningFrontPage',
      label: 'Richiamo in prima pagina',
      max: 3
    }, {
      key: 'positioningTitleMention',
      label: 'Cliente nel titolo',
      max: 3
    }, {
      key: 'positioningBold',
      label: 'Citazione in grassetto',
      max: 2
    }, {
      key: 'positioningPhoto',
      label: 'Foto presente',
      max: 2
    }]
  }, {
    title: 'Tipologia contenuto',
    description: "Peso in base alla profondità dell'articolo.",
    items: [{
      key: 'contentInterview',
      label: 'Intervista',
      max: 3
    }, {
      key: 'contentSignedCommentary',
      label: 'Commento firmato',
      max: 3
    }, {
      key: 'contentDedicatedArticle',
      label: 'Articolo dedicato',
      max: 3
    }, {
      key: 'contentComment',
      label: 'Commento',
      max: 2
    }, {
      key: 'contentMention',
      label: 'Citazione',
      max: 2
    }]
  }, {
    title: 'Sentiment',
    description: "Modifica il punteggio in base al tono dell'articolo.",
    items: [{
      key: 'sentimentPositive',
      label: 'Sentiment positivo',
      max: 2
    }, {
      key: 'sentimentNeutral',
      label: 'Sentiment neutro',
      max: 2
    }, {
      key: 'sentimentNegative',
      label: 'Sentiment negativo',
      max: 2
    }]
  }, {
    title: 'Altro',
    description: '',
    items: [{
      key: 'authoritativeSignature',
      label: 'Firma autorevole',
      description: 'Bonus per articoli firmati da giornalisti autorevoli',
      max: 2
    }]
  }];
  return <div className="p-6 bg-muted/10 border-t border-border" data-id="element-1416">
      <div className="flex items-center justify-between mb-6" data-id="element-1417">
        <h4 className="font-medium" data-id="element-1418">Modifica pesi</h4>
        <div className="flex gap-2" data-id="element-1419">
          <Button variant="outline" size="sm" onClick={() => setWeights(defaultWeights)} className="rounded-full border-accent text-accent hover:bg-accent/10 hover:text-accent" data-id="element-1420">
            Ripristina default
          </Button>
          <Button size="sm" onClick={() => onSave(weights)} className="rounded-full" data-id="element-1421">
            Salva modifiche
          </Button>
        </div>
      </div>
      <div className="space-y-10" data-id="element-1422">
        {sections.map(section => <div key={section.title} data-id="element-1423">
            <div className="mb-4" data-id="element-1424">
              <h3 className="text-sm font-semibold mb-1" data-id="element-1425">{section.title}</h3>
              {section.description && <p className="text-xs text-muted-foreground" data-id="element-1426">
                  {section.description}
                </p>}
            </div>
            <div className="divide-y divide-border border-y border-border" data-id="element-1427">
              {section.items.map(item => <WeightRow key={item.key} label={item.label} description={item.description} value={weights[item.key]} onChange={v => update(item.key, v)} max={item.max} data-id="element-1428" />)}
            </div>
          </div>)}
      </div>
    </div>;
}
function AlgorithmTab() {
  const [profiles, setProfiles] = useState<Profile[]>(initialProfiles);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const handleActivate = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setProfiles(profiles.map(p => ({
      ...p,
      isActive: p.id === id
    })));
  };
  const handleCreateProfile = () => {
    if (!newProfileName.trim()) return;
    const newProfile: Profile = {
      id: Math.random().toString(36).substring(7),
      name: newProfileName,
      isActive: false,
      weights: defaultWeights,
      createdAt: new Date().toISOString()
    };
    setProfiles([...profiles, newProfile]);
    setNewDialogOpen(false);
    setNewProfileName('');
  };
  const handleSaveWeights = (id: string, newWeights: AlgorithmWeights) => {
    setProfiles(profiles.map(p => p.id === id ? {
      ...p,
      weights: newWeights
    } : p));
    setExpandedId(null);
  };
  return <div data-id="element-1429">
      <div className="mb-8 flex flex-col md:flex-row md:items-start md:justify-between gap-3" data-id="element-1430">
        <div data-id="element-1431">
          <h2 className="text-lg font-semibold mb-1" data-id="element-1432">Profili algoritmo</h2>
          <p className="text-sm text-muted-foreground max-w-xl" data-id="element-1433">
            Gestisci i profili dei pesi per l'algoritmo di rilevanza. Attiva un
            profilo per applicarlo ai nuovi articoli.
          </p>
        </div>
        <div className="flex gap-2.5 flex-shrink-0" data-id="element-1434">
          <Button onClick={() => setNewDialogOpen(true)} className="rounded-full h-11 px-6 text-sm" data-id="element-1435">
            <Plus className="h-4 w-4 mr-1.5" data-id="element-1436" />
            Nuovo profilo
          </Button>
        </div>
      </div>

      <div className="border border-border rounded-lg overflow-hidden divide-y divide-border" data-id="element-1437">
        {profiles.map(profile => {
        const isExpanded = expandedId === profile.id;
        const customizedCount = getCustomizedCount(profile.weights);
        return <div key={profile.id} className="flex flex-col bg-card" data-id="element-1438">
              <div className={`flex items-center justify-between p-4 md:p-6 cursor-pointer transition-colors hover:bg-muted/30 ${profile.isActive ? 'border-l-4 border-l-accent' : 'border-l-4 border-l-transparent'}`} onClick={() => setExpandedId(isExpanded ? null : profile.id)} data-id="element-1439">
                <div className="flex items-center gap-4" data-id="element-1440">
                  <div data-id="element-1441">
                    <div className="flex items-center gap-3 mb-1" data-id="element-1442">
                      <h3 className="font-medium text-base" data-id="element-1443">{profile.name}</h3>
                      {profile.isActive && <Badge variant="default" className="bg-accent text-accent-foreground hover:bg-accent/90" data-id="element-1444">
                          Attivo
                        </Badge>}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground" data-id="element-1445">
                      <span data-id="element-1446">
                        Creato il{' '}
                        {new Date(profile.createdAt).toLocaleDateString('it-IT')}
                      </span>
                      <span data-id="element-1447">·</span>
                      <span data-id="element-1448">{customizedCount} pesi personalizzati</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4" data-id="element-1449">
                  {!profile.isActive && <Button variant="outline" size="sm" className="rounded-full" onClick={e => handleActivate(e, profile.id)} data-id="element-1450">
                      Attiva
                    </Button>}
                  <div className="text-muted-foreground" data-id="element-1451">
                    {isExpanded ? <ChevronUp className="h-5 w-5" data-id="element-1452" /> : <ChevronDown className="h-5 w-5" data-id="element-1453" />}
                  </div>
                </div>
              </div>

              <AnimatePresence data-id="element-1454">
                {isExpanded && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="overflow-hidden" data-id="element-1455">
                    <ProfileEditor profile={profile} onSave={w => handleSaveWeights(profile.id, w)} onCancel={() => setExpandedId(null)} data-id="element-1456" />
                  </motion.div>}
              </AnimatePresence>
            </div>;
      })}
      </div>

      <Dialog open={newDialogOpen} onOpenChange={setNewDialogOpen} data-id="element-1457">
        <DialogContent className="sm:max-w-[425px]" data-id="element-1458">
          <DialogHeader data-id="element-1459">
            <DialogTitle data-id="element-1460">Nuovo profilo algoritmo</DialogTitle>
            <DialogDescription data-id="element-1461">
              Inserisci un nome per il nuovo profilo. Verrà creato con i pesi di
              default.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4" data-id="element-1462">
            <input type="text" placeholder="Nome profilo..." value={newProfileName} onChange={e => setNewProfileName(e.target.value)} className="w-full flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" autoFocus data-id="element-1463" />
          </div>
          <div className="flex justify-end gap-2" data-id="element-1464">
            <Button variant="outline" onClick={() => setNewDialogOpen(false)} className="rounded-full" data-id="element-1465">
              Annulla
            </Button>
            <Button onClick={handleCreateProfile} className="rounded-full" disabled={!newProfileName.trim()} data-id="element-1466">
              Crea profilo
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>;
}
export function Settings() {
  const location = useLocation();
  const isAlgorithm = location.pathname.includes('algorithm');
  return <div className="px-6 py-6 max-w-[1400px] mx-auto w-full" data-id="element-1467">
      <header className="mb-8" data-id="element-1468">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-id="element-1469">
          {isAlgorithm ? 'Algoritmo' : 'Connettori'}
        </h1>
      </header>

      <div className="bg-card border border-border rounded-lg p-6" data-id="element-1470">
        {isAlgorithm ? <AlgorithmTab data-id="element-1471" /> : <ConnectorsTab data-id="element-1472" />}
      </div>
    </div>;
}